import { useEffect, useRef } from "react";
import { type CompareV2DispatchType, useCompareV2Selector } from "../redux/store";
import { getModelGradeBodyTypeIdsAndEngineIdsFromUrl } from "../utils/utils";
import { getPostal } from "../../../common-deprecated/utils/postalUtils";
import { POSTAL_CHANNEL_T1_COMPARE_V2, POSTAL_TOPIC_T1_COMPARE_V2_ADD_CAR } from "../../../common-deprecated/constants";
import type { PostalNewDataType } from "../utils/compareTypes";
import { CompareV2Mode } from "../../../shared-logic/features/compare/utils/types";
import type {
    CompareMotorizationType,
    ModelGradeBodyTypeIdsType,
} from "../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import { compareAddCar } from "../../../common-deprecated/features/compare/redux/actions/NewCarCompareExternalDataActions";
import Debug from "../../../common-deprecated/Debug";
import { getCompareLocalStorage } from "../utils/localStorage";
import { useDispatch } from "../../../shared-logic/redux/store";

export const getParsedAndLimitedData = (data: {
    modelGradeBodyTypeIds: ModelGradeBodyTypeIdsType[];
    motorizationIds: CompareMotorizationType[];
    colourIds: string[];
}): { ids: ModelGradeBodyTypeIdsType; motorization: CompareMotorizationType; colour: string }[] => {
    const { modelGradeBodyTypeIds, motorizationIds, colourIds } = data;
    if (
        modelGradeBodyTypeIds.length &&
        modelGradeBodyTypeIds.length === motorizationIds.length &&
        motorizationIds.length === colourIds.length
    ) {
        return modelGradeBodyTypeIds.map((_, index) => ({
            ids: modelGradeBodyTypeIds[index],
            motorization: motorizationIds[index],
            colour: colourIds[index],
        }));
    }
    return [];
};

const useInitNewCompare = (): void => {
    const settings = useCompareV2Selector(({ commonSettings }) => commonSettings);
    const compareContext = useCompareV2Selector((state) => state.newCarCompare.ncCompareContext);
    const dispatch = useDispatch<CompareV2DispatchType>();

    useEffect(() => {
        if (settings.query.compareData) {
            const data = getModelGradeBodyTypeIdsAndEngineIdsFromUrl(settings.query.compareData);
            const cars = getParsedAndLimitedData(data);
            cars.forEach((car, index) => {
                dispatch(compareAddCar(car.ids, car.motorization, index, car.colour));
            });
        }
    }, []);

    const postalSubscription = useRef<any>(null);
    useEffect(() => {
        postalSubscription.current = getPostal().subscribe({
            channel: POSTAL_CHANNEL_T1_COMPARE_V2,
            topic: POSTAL_TOPIC_T1_COMPARE_V2_ADD_CAR,
            callback: (data: PostalNewDataType) => {
                if (
                    (data.mode === CompareV2Mode.New || !data.mode) &&
                    data.gradeId &&
                    data.modelId &&
                    data.bodyTypeId
                ) {
                    let engineIds: CompareMotorizationType | null = null;
                    if (data.engineId && data.wheeldriveId && data.transmissionId) {
                        engineIds = {
                            engineId: data.engineId,
                            wheeldriveId: data.wheeldriveId,
                            transmissionId: data.transmissionId,
                            engineName: "",
                            wheeldriveName: "",
                            transmissionName: "",
                        };
                    }

                    dispatch(
                        compareAddCar(
                            {
                                model: data.modelId,
                                submodelId: data.submodelId || "",
                                bodyTypeId: data.bodyTypeId,
                                gradeId: data.gradeId,
                            },
                            engineIds,
                            // We used to look at props.cars.length but this is run when component mounts, so it is always 0.
                            // By setting it to -1 we will check in the logic how many cars we have in the compare.
                            -1,
                            data.colourCode || data.colourId, // both code as guid are supported in getColourCode util
                        ),
                    );
                } else {
                    Debug.error(
                        `Could not add car to compare because not all ID's are sent with postal: ${JSON.stringify(
                            data,
                        )}`,
                    );
                }
            },
        });

        if (!settings.query.compareData) {
            const { modelGradeBodyTypeIds, motorizationIds, colourIds } = getCompareLocalStorage(
                settings,
                compareContext,
            );
            const cars = getParsedAndLimitedData({
                modelGradeBodyTypeIds,
                motorizationIds,
                colourIds,
            });
            cars.forEach((car, index) => dispatch(compareAddCar(car.ids, car.motorization, index, car.colour)));
        }

        return () => {
            if (postalSubscription.current) {
                // Unsubscribe from postal, we want to stop dispatches when switched away from mode
                getPostal().unsubscribe(postalSubscription.current);
                postalSubscription.current = null;
            }
        };
    }, [dispatch, settings]);
};

export default useInitNewCompare;
